import { useRef, useState, useMemo, useEffect } from 'react'
import { TableColumnConfigType } from '../types'
import { Button, Col, Input, InputRef, Menu, Row } from 'antd'
import { FilterConfirmProps, FilterValue } from 'antd/lib/table/interface'
import {
  SearchOutlined,
  DownCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  SortDescendingOutlined,
  SortAscendingOutlined,
} from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import styles from '../Table.module.scss'
import { useTranslation } from 'react-i18next'
import { InputField } from '@shared/InputField'

const SearchInput = ({ onChange }) => {
  const searchInlineInput = useRef<InputRef>(null)

  useEffect(() => {
    if (searchInlineInput) {
      searchInlineInput.current?.focus()
    }
  }, [searchInlineInput])

  return (
    <Input
      ref={searchInlineInput}
      size="small"
      className={styles.searchInput}
      onChange={onChange}
    />
  )
}

export function useConfigColumns<T extends object>(
  columns: T,
  sortedData: T[]
) {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef<InputRef>(null)
  const [filteredInfo, setFilteredInfo] = useState<
    Record<string, FilterValue | null>
  >({})

  const handleSearch = (
    selectedKeys: string,
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: any
  ) => {
    setSearchText(selectedKeys || '')
    setSearchedColumn(dataIndex)
    confirm({ closeDropdown: false })
  }

  const [sortConfig, setSortConfig] = useState<{
    field: string
    order: 'asc' | 'desc' | null
  }>({
    field: '',
    order: null,
  })

  sortedData = useMemo(() => {
    if (!sortConfig.field || !sortConfig.order) return sortedData

    return [...sortedData].sort((a, b) => {
      const valA = a[sortConfig.field]
      const valB = b[sortConfig.field]

      if (valA === undefined || valB === undefined) return 0

      const compare = typeof valA === 'number' && typeof valB === 'number'
        ? valA - valB
        : String(valA).localeCompare(String(valB))
        
      return sortConfig.order === 'asc' ? compare : -compare
    })
  }, [sortConfig, sortedData])

  const handleSelect = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: any
  ) => {
    setSearchText(selectedKeys[0] || '')
    setSearchedColumn(dataIndex)
    confirm()
  }

  const handleReset = (clearFilters: () => void) => {
    clearFilters()
    setSearchText('')
  }

  const generateFilterDropdown = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
    coreConfig,
    dataIndex,
    options,
    i18nKeyForLabel,
  }: {
    setSelectedKeys: (keys: React.Key[]) => void
    selectedKeys: React.Key[]
    confirm: (param?: FilterConfirmProps) => void
    clearFilters: () => void
    coreConfig: any
    dataIndex: string
    options?: any[]
  }) => (
    <Row
      style={{
        width: '100%',
        padding: 8,
        gap: 5,
        flexDirection: 'column',
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Col>
        {options ? (
          <Menu
            onClick={(evt) => {
              setSelectedKeys(evt.key ? [evt.key] : [])
              handleSelect(selectedKeys as string[], confirm, dataIndex)
            }}
            items={[
              {
                label: t('orders.salaryCardOrder.inputLabels.choose'),
                key: '',
              },
              ...options.map(
                (item) =>
                  item && {
                    key: item,
                    label: i18nKeyForLabel
                      ? t(`${i18nKeyForLabel}.${item}`)
                      : item,
                  }
              ),
            ]}
          />
        ) : (
          <InputField
            size="small"
            validateStatus="validating"
            ref={searchInput}
            placeholder={`Axtar: ${t(
              `orders.salaryCardOrder.tableTitles.${dataIndex}`
            )}`}
            value={`${selectedKeys || ''}`}
            onChange={(value) => {
              setSelectedKeys(value ? [value] : value)
              handleSearch(value, confirm, dataIndex)
            }}
          />
        )}
      </Col>
    </Row>
  )

  const cols = useMemo(() => {
    return Object.entries(columns).reduce<TableColumnConfigType<T>[]>(
      (acc, [dataIndex, columnConfig]) => {
        const {
          hasSearch,
          isFile,
          maskOptions,
          title,
          hasSelect,
          hasSorter,
          editable,
          i18nKeyForLabel = null,
          renderText,
          ...coreConfig
        } = columnConfig as TableColumnConfigType<T>

        if (hasSelect) {
          const options = Array.from(
            new Set(sortedData.map((item) => item[dataIndex]))
          )
          coreConfig.filterDropdown = ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
          }) =>
            generateFilterDropdown({
              setSelectedKeys,
              selectedKeys,
              confirm,
              clearFilters,
              coreConfig,
              dataIndex,
              options,
              i18nKeyForLabel,
            })
          coreConfig.filterIcon = () => (
            <DownCircleOutlined className={styles.icon} />
          )
          coreConfig.onFilter = (value, record) =>
            record[dataIndex]
              ?.toString()
              .toLowerCase()
              .startsWith((value as string).toLowerCase())
        }

        if (hasSearch) {
          // coreConfig.filterDropdown = ({
          //   setSelectedKeys,
          //   selectedKeys,
          //   confirm,
          //   clearFilters,
          // }) =>
          //   generateFilterDropdown({
          //     setSelectedKeys,
          //     selectedKeys,
          //     confirm,
          //     clearFilters,
          //     coreConfig,
          //     dataIndex,
          //   })
          // coreConfig.filterIcon = () => (
          //   <SearchOutlined className={styles.icon} />
          // )
          coreConfig.onFilter = (value, record) =>
            record[dataIndex]
              ?.toString()
              .toLowerCase()
              .includes((value as string).toLowerCase())
          // coreConfig.onFilterDropdownOpenChange = (visible) => {
          //   if (visible) {
          //     setTimeout(() => searchInput.current?.select(), 100)
          //   }
          // }
          coreConfig.render = (text, record, index) => {
            const filteredValue = filteredInfo[dataIndex] ? (
              <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={filteredInfo[dataIndex]}
                autoEscape
                textToHighlight={text ? text.toString() : ''}
              />
            ) : (
              text
            )

            return renderText
              ? renderText(filteredValue, record, index)
              : filteredValue
          }
        }

        if (editable) {
          coreConfig.onCell = (record, index) => ({
            index,
            record,
            ellipsis: coreConfig.ellipsis,
            editable,
            maskOptions,
            isSelect: hasSelect,
            isFile,
            selectOptions: coreConfig.filterSelectOptions,
            dataIndex,
            title: coreConfig.title,
            type: coreConfig.type ?? 'string',
          })
        }

        if (filteredInfo[dataIndex]) {
          coreConfig.className = styles.removeFilterIcon
        }

        acc.push({
          dataIndex,
          title: (
            <Row justify="center" align="middle" gutter={10}>
              {hasSorter && (
                <Button
                  icon={
                    sortConfig.field === dataIndex &&
                    sortConfig.order === 'desc' ? (
                      <SortDescendingOutlined style={{ fontSize: 12 }} />
                    ) : (
                      <SortAscendingOutlined style={{ fontSize: 12 }} />
                    )
                  }
                  style={{
                    backgroundColor: !(
                      sortConfig.field === dataIndex && sortConfig.order
                    )
                      ? 'transparent'
                      : '#fde280',
                    border: 0,
                    width: 18,
                    height: 18,
                  }}
                  onClick={() => {
                    setSortConfig((prev) => {
                      if (prev.field !== dataIndex) {
                        return { field: dataIndex, order: 'asc' }
                      }

                      if (prev.order === 'asc')
                        return { field: dataIndex, order: 'desc' }
                      if (prev.order === 'desc')
                        return { field: '', order: null }
                      return { field: dataIndex, order: 'asc' }
                    })
                  }}
                />
              )}
              <Col className="ant-table-column-title">
                {hasSearch && filteredInfo[dataIndex] ? (
                  <SearchInput
                    onChange={(e) => {
                      setFilteredInfo({
                        ...filteredInfo,
                        [dataIndex]: [e.target.value],
                      })
                    }}
                  />
                ) : filteredInfo[dataIndex] ? (
                  i18nKeyForLabel ? (
                    t(`${i18nKeyForLabel}.${filteredInfo[dataIndex]}`)
                  ) : (
                    filteredInfo[dataIndex]
                  )
                ) : (
                  title
                )}
              </Col>
              {filteredInfo[dataIndex] ? (
                <Col
                  className="ant-dropdown-trigger ant-table-filter-trigger remove-icon"
                  style={{ display: 'none' }}
                  onClick={() => {
                    setSearchText('')
                    setFilteredInfo({ ...filteredInfo, [dataIndex]: null })
                  }}
                >
                  <CloseCircleOutlined style={{ color: '#f3646c' }} />
                </Col>
              ) : (
                hasSearch && (
                  <Col
                    className="ant-dropdown-trigger ant-table-filter-trigger remove-icon"
                    onClick={() => {
                      setFilteredInfo({ ...filteredInfo, [dataIndex]: [] })
                    }}
                  >
                    <SearchOutlined />
                  </Col>
                )
              )}
            </Row>
          ),
          filteredValue: filteredInfo[dataIndex] || null,
          key: dataIndex,
          ...coreConfig,
        })

        return acc
      },
      []
    )
  }, [columns, sortedData, filteredInfo, searchText, searchedColumn])

  return { columns: cols, sortedData, filteredInfo, setFilteredInfo }
}
